import apiClient from '../client';
import ENDPOINTS from '../endpoints';

const profileService = {
  /* Logged-in room parent's own record — user_metadata carries name, phone,
     profilePic, kids, interests, and schoolClassDetails (drives the context
     switcher). */
  myProfile: () => apiClient.get(ENDPOINTS.ROOM_PARENT_ADMIN.MY_PROFILE),

  /* The interest chips, grouped: [{ id, label, emoji, items: [...] }]. Server-
     owned, so a tag written here matches what the phone app writes. */
  activities: async () => {
    const res = await apiClient.get(ENDPOINTS.USER.ACTIVITIES);
    const list = res?.data?.data || res?.data || [];
    return (Array.isArray(list) ? list : []).map((c) => ({
      id: c.id,
      label: c.label || '',
      emoji: c.emoji || '',
      items: (c.items || []).map((i) => ({ id: i.id, label: i.label || '', emoji: i.emoji || '' })),
    }));
  },

  /* Sign-up's final write. Besides the metadata it enrolls the account in the
     chosen grade networks and flips login_step to "3", so it belongs to
     onboarding only — running it again for an edit would re-insert those
     memberships and leave duplicates behind. Edits go through
     updateParentProfile instead.

     tags/childData/schoolClass/location travel as JSON strings inside multipart
     form data (the server JSON.parses exactly those four), and photos ride along
     as files. Content-Type must be cleared or axios serializes the FormData to
     JSON and drops them. */
  saveParentProfile: ({
    firstName,
    lastName = '',
    zipCode = '',
    role = '',
    location = null,
    tags = [],
    hobbies = '',
    about = '',
    kids = [],
    schoolClass = [],
    profilePic = null,
  }) => {
    const fd = new FormData();
    fd.append('firstName', firstName);
    fd.append('lastName', lastName);
    fd.append('zipCode', zipCode);
    fd.append('Iam', role);
    /* the API requires jobTitle; the phone app sends the parenting role there */
    fd.append('jobTitle', role);
    fd.append('companyName', '');
    fd.append('about', about);
    fd.append('hobbies', hobbies);
    fd.append('linkedIn', '');
    fd.append('question3', '');
    fd.append('question4', '');
    fd.append('question5', '');
    fd.append('work_experience', '');
    fd.append('tags', JSON.stringify(tags));
    fd.append('location', JSON.stringify(location || {}));
    fd.append('schoolClass', JSON.stringify(schoolClass));
    /* imagePath is the matching file's name — that's how the server pairs each
       uploaded childProfilePic back to its child. Kids already carrying an http
       imagePath keep it; the server leaves those alone. */
    fd.append(
      'childData',
      JSON.stringify(
        kids.map(({ id, childName, birthDate, gender, imagePath }) => ({
          id,
          childName,
          birthDate,
          gender,
          imagePath: imagePath || '',
        }))
      )
    );
    if (profilePic) fd.append('profilePic', profilePic);
    kids.forEach((k) => {
      if (k.file) fd.append('childProfilePic', k.file, k.imagePath);
    });
    return apiClient.patch(ENDPOINTS.USER.AI_PARENT_PROFILE, fd, {
      headers: { 'Content-Type': undefined },
      timeout: 120000,
    });
  },

  /* Editing an account that's already set up. Merges into user_metadata and
     touches nothing else — no network membership, no login_step.

     The route validates strictly, so these nine fields are the only ones it
     accepts; anything extra is a 400. childData must always be sent (the server
     maps over it unguarded), and empty strings are dropped server-side, so
     blanking a text field leaves the old value in place. */
  updateParentProfile: ({
    firstName,
    lastName = '',
    about = '',
    tags = [],
    kids = [],
    zipCode = '',
    role = '',
    location = null,
    profilePic = null,
  }) => {
    const fd = new FormData();
    fd.append('firstName', firstName);
    fd.append('lastName', lastName);
    fd.append('about', about);
    fd.append('zipCode', zipCode);
    fd.append('Iam', role);
    fd.append('jobTitle', role);
    fd.append('tags', JSON.stringify(tags));
    fd.append('location', JSON.stringify(location || {}));
    fd.append(
      'childData',
      JSON.stringify(
        kids.map(({ id, childName, birthDate, gender, imagePath }) => ({
          id,
          childName,
          birthDate,
          gender,
          imagePath: imagePath || '',
        }))
      )
    );
    if (profilePic) fd.append('profilePic', profilePic);
    kids.forEach((k) => {
      if (k.file) fd.append('childProfilePic', k.file, k.imagePath);
    });
    return apiClient.patch(ENDPOINTS.USER.PARENT_PROFILE_UPDATE, fd, {
      headers: { 'Content-Type': undefined },
      timeout: 120000,
    });
  },
};

export default profileService;
